import { ReactNode } from 'react';
import {
  Button as AriaButton,
  Dialog,
  DialogTrigger,
  Popover,
} from 'react-aria-components';
import { Filter, X } from 'lucide-react';
import { cx } from './cx';
import { Icon } from './Icon';
import { Text, typography } from './Text';
import { useT } from '../../modules/i18n';

export interface ColumnFilterProps {
  /** Column header label (kept in the uppercase header style). */
  label: ReactNode;
  /** Whether a filter is currently applied — tints the header + funnel brand. */
  active?: boolean;
  align?: 'left' | 'right' | 'center';
  /** Clears this column's filter; renders a Clear action when `active`. */
  onClear?: () => void;
  /** Tailwind width class for the popover panel (default `w-60`). */
  width?: string;
  /** The filter controls, or a render fn receiving a `close` callback. */
  children: ReactNode | ((close: () => void) => ReactNode);
}

const popover =
  'rounded-lg border border-default bg-surface shadow-lg outline-none ' +
  'entering:animate-in entering:fade-in-0 entering:zoom-in-95 exiting:animate-out exiting:fade-out-0';

/**
 * A filterable column header — the column label with an inline funnel that
 * opens a popover hosting the column's own filter controls (Checkbox,
 * FilterChips, TextInput, …). Brand-tinted while a filter is applied, with a
 * Clear action. Lets a table filter per column in place; compose it as a
 * DataTable column `header`.
 */
export const ColumnFilter = ({
  label,
  active,
  align = 'left',
  onClear,
  width = 'w-60',
  children,
}: ColumnFilterProps) => {
  const t = useT();
  return (
    <DialogTrigger>
      <AriaButton
        aria-label={
          typeof label === 'string'
            ? t('ui.columnFilter.filterByLabel', { label })
            : t('ui.columnFilter.filterColumnAriaLabel')
        }
        className={cx(
          'group/cf appearance-none bg-transparent inline-flex items-center gap-1.5 rounded outline-none cursor-pointer transition-colors',
          typography.overline,
          'data-[focus-visible]:ring-2 data-[focus-visible]:ring-[rgb(var(--c-ring)/0.4)] data-[focus-visible]:ring-offset-2',
          active
            ? 'text-brand-text'
            : 'text-fg-tertiary hover:text-fg-secondary',
        )}
      >
        <span className="truncate">{label}</span>
        <Icon
          as={Filter}
          size="xs"
          className={cx(
            'shrink-0 transition-colors',
            active
              ? 'text-brand-text'
              : 'text-fg-muted group-hover/cf:text-fg-tertiary',
          )}
        />
      </AriaButton>
      <Popover
        className={popover}
        placement={align === 'right' ? 'bottom end' : 'bottom start'}
      >
        <Dialog
          className={cx('flex flex-col gap-2 p-3 outline-none', width)}
          aria-label={t('ui.columnFilter.dialogAriaLabel')}
        >
          {({ close }) => (
            <>
              <div className="flex items-center justify-between gap-6">
                <Text variant="overline" tone="tertiary">
                  {t('ui.columnFilter.filterLabel')}
                </Text>
                {active && onClear && (
                  <button
                    type="button"
                    onClick={onClear}
                    className={cx(
                      typography.caption,
                      'font-semibold inline-flex items-center gap-1 text-fg-tertiary hover:text-fg cursor-pointer',
                    )}
                  >
                    <Icon as={X} size="xs" /> {t('ui.columnFilter.clear')}
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-2">
                {typeof children === 'function' ? children(close) : children}
              </div>
            </>
          )}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
};
